// import ViewButton from '../../../lib/numu_library/view-button';
import { ViewButton } from '@lua-frontend/numu-ui';
import axios from 'axios';
import React, { useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { FormattedMessage, IntlProvider, addLocaleData } from 'react-intl';
import en from 'react-intl/locale-data/en';
import es from 'react-intl/locale-data/es';
import {
  FileUploaderWrapper,
  activeStyle,
  baseStyle,
} from './file-uploader.style';
import { defaultAcceptedFiles, getFileType } from './utils';
import messages from './utils/locale';

// Initialize locale
addLocaleData(en);
addLocaleData(es);

/**
 * @description
 * FileUploader is a bit dev component for uploading or receiving raw files using drag and drop.
 * Libraries - react-dropzone, react-intl, @numudev/numu_library.view-button
 *
 * @param props
 * @param onCancel.isFunction.isRequired - Used to close the FileUploader component
 * @param onReturnFiles.isFunction.isRequired - call when we have final urls to be used by the parent component
 * @param allowUpload.isBoolean.isOptional.defaultValue[False]
 * - If true, the component uploads the file on S3 and returns list of final cloudfront urls
 * - If false, the component returns list of raw acceptedFiles
 * @param texts.isObject.isOptional.defaultValue[emptyObject] - This object contains relevant texts to display on drag and drop zone
 * @param language.isString.isOptional.defaultValue[en] - For rendering the texts in translated formats
 * @param accept.isObject.isOptional.defaultValue[emptyObject] - For defining which selective files can be uploaded
 *
 * @returns
 * A drag and drop zone with ability to upload files
 */
const FileUploader = ({
  onCancel,
  onReturnFiles,
  allowUpload = false,
  texts = {},
  language = 'en',
  accept = defaultAcceptedFiles,
  fileName = '',
  signedUrlRoute = '',
}) => {
  const [files, setFiles] = useState([]);
  const [showError, toggleError] = useState(false);

  const getS3Urls = async (files) => {
    let targetFile = null;
    let fileType = null;

    const contentToUpload = [];
    const allImages = files;

    for (let i = 0; i < allImages.length; i++) {
      targetFile = allImages[i];
      const randomId = parseInt(Math.random() * 1000000, 10);

      if (targetFile) {
        fileType = targetFile.type || '';
        const filePathOrName = targetFile.path || targetFile.name;
        const { extension: fileExtension, type: fileSaveType } = getFileType(
          filePathOrName,
          fileType
        );

        /**
         * Changing the 'type' attribute to 'mov' for quicktime videos
         */
        if (fileType === 'video/quicktime') {
          // new File([file], 'file-name.extension', { type: extension })
          targetFile = new File([targetFile], targetFile.name, {
            type: 'video/mov',
          });
        }

        // val = await getBase64(targetFile);

        contentToUpload.push({
          size: targetFile.size,
          type: targetFile.type,
          fileType: fileSaveType,
          preview: URL.createObjectURL(targetFile),
          fileName: targetFile.name,
          // url: val,
          name: `${randomId}.${fileExtension}`,
          path: `chat/attachment/`,
          targetFile,
        });
      }
    }

    try {
      const finalUpload = contentToUpload.filter(
        (file) => file.fileType !== 'unknown'
      );

      if (finalUpload.length !== contentToUpload.length) {
        onDropRejected();
        return false;
      } else if (finalUpload.length) {
        const contentToUpload = finalUpload.map((item) => {
          return {file: item?.targetFile , name: item.name,type: item.type,preview: item.preview}
        });
        return contentToUpload;
      }

      return [];
    } catch (error) {
      console.log(error);
    }
  };

  const onDropAccepted = async (acceptedFiles) => {
    const signedUrls = await getS3Urls(acceptedFiles);

    if (signedUrls) {
      if (allowUpload) {
        setFiles([...files, ...signedUrls]);
      } else {
        onReturnFiles([...files, ...signedUrls]);
        onCancel();
      }
    }
  };

  const onDropRejected = () => {
    toggleError(true);
    setTimeout(() => toggleError(false), 3000);
  };

  const dropZoneParams = { onDropAccepted, onDropRejected };
  if (accept && Object.keys(accept).length > 0) {
    dropZoneParams.accept = accept;
  }
  const { getRootProps, getInputProps, isDragActive, isDragReject } =
    useDropzone(dropZoneParams);

  function renderCancelButton() {
    return (
      <ViewButton
        classNames="cancel-btn"
        onClick={(e) => {
          e.stopPropagation();
          onCancel();
        }}
        text={<FormattedMessage id="cancel" />}
      />
    );
  }

  function renderDraggedFiles() {
    return files.map((file) => (
      <div key={file.path} style={{ display: 'flex', alignItems: 'center' }}>
        <div style={{ width: 200, height: 10, background: '#ccc' }} />
        <span style={{ marginLeft: 10 }}>{file.path}</span>
      </div>
    ));
  }

  function renderDragDiv() {
    const renderText = () => {
      if (isDragReject || showError) {
        return texts && texts.reject ? (
          texts.reject
        ) : (
          <FormattedMessage id="drag-text-reject" />
        );
      }

      return texts && texts.default ? (
        texts.default
      ) : (
        <FormattedMessage id="drag-text-default" />
      );
    };

    return (
      <div className="drag-div" {...getRootProps({ style })}>
        <input {...getInputProps()} />
        <div className="drag-text">{renderText()}</div>
      </div>
    );
  }

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isDragActive ? activeStyle : {}),
    }),
    [isDragActive]
  );

  return (
    <IntlProvider locale={language} messages={messages[language]}>
      <FileUploaderWrapper>
        <div className="drag-wrapper flex-center">
          {allowUpload && renderDraggedFiles()}
          {renderDragDiv()}
        </div>
        {renderCancelButton()}
      </FileUploaderWrapper>
    </IntlProvider>
  );
};

export { FileUploader };
